import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { ControlerEnum } from '../enums/controler.enum';
import { ICountryList, ICountryListData, ICountryListPaginationResult, ICountryPagination, ICreateCountry, ICreateCountryResult, IDeleteCountry, IGetCountry, IUpdateCountry } from '../interfaces/country.interface';
import { AuthenticationService } from './authentication.service';
import { IToken } from '../interfaces/token.interface';
import { Roles } from '../enums/roles.enum';

@Injectable({
  providedIn: 'root',
})
export class CountryService {
  apiBaseUrl: string = environment.apiBaseUrl;
  private http = inject(HttpClient);
  private readonly authenticationService = inject(AuthenticationService);

  getPaginatedCountries(data: ICountryPagination) {
    let query = `?pageSize=${data.pageSize}&pageNumber=${data.pageNumber}`;

    if (data.fullName) {
      query += `&fullName=${data.fullName}`;
    }

    if (data.countryCode) {
      query += `&countryCode=${data.countryCode}`;
    }

    if (data.vat) {
      query += `&vat=${data.vat}`;
    }

    return this.http.get<ICountryListPaginationResult>(
      `${this.apiBaseUrl}/${ControlerEnum.Country}/paginated${query}`,
      { observe: 'response' }
    );
  }

  getCountries(): Observable<ICountryListData[]> {
    return this.http.get<ICountryList>(
      `${this.apiBaseUrl}/${ControlerEnum.Country}`,
      { observe: 'response' },
    ).pipe(
      map((response: HttpResponse<ICountryList>) => {
        const token: IToken = this.authenticationService.extractToken();
        const role = token.role;
        const countryId = token.countryId;
        const countries = response.body?.result || [];

        if (role === Roles.CountryAdmin || role === Roles.VendorAdmin) {
          return countries.filter((country) => country.id === +countryId);
        }

        return countries;
      })
    );
  }

  createCountry(
    model: ICreateCountry,
  ): Observable<HttpResponse<ICreateCountryResult>> {
    return this.http.post<ICreateCountryResult>(
      `${this.apiBaseUrl}/${ControlerEnum.Country}`,
      model,
      { observe: 'response' },
    );
  }

  deleteCountry(
    id: number
  ): Observable<HttpResponse<IDeleteCountry>> {
    return this.http.delete<IDeleteCountry>(
      `${this.apiBaseUrl}/${ControlerEnum.Country}/${id}`,
      { observe: 'response' },
    );
  }

  updateCountry(
    model: IUpdateCountry,
  ): Observable<HttpResponse<ICreateCountryResult>> {
    return this.http.put<ICreateCountryResult>(
      `${this.apiBaseUrl}/${ControlerEnum.Country}`,
      model,
      { observe: 'response' },
    );
  }

  getCountryById(countryId: number): Observable<HttpResponse<IGetCountry>> {
    return this.http.get<IGetCountry>(
      `${this.apiBaseUrl}/${ControlerEnum.Country}/${countryId}`,
      { observe: 'response' },
    );
  }

  changeCountry(
    countryId: number,
    refreshToken: string,
  ): Observable<HttpResponse<any>> {
    const model = {
      countryId: countryId,
      refreshToken: refreshToken,
    };
    return this.http.post<any>(
      `${this.apiBaseUrl}/${ControlerEnum.Country}/changeCountry`,
      model,
      { observe: 'response' },
    );
  }
}
